import { useEffect, useRef, useState } from 'react';
import { Search, ShoppingBag, Award, Settings, Users, Fish, Check, X, Plus, Coins, ArrowUpDown, ChevronDown, Wrench, Cat, Volume2, VolumeX, Lock, LockOpen, Delete, Timer, Square, LayoutGrid, Grid3X3, BarChart3 } from 'lucide-react';
import { match } from 'pinyin-pro';
import { useTranslation } from 'react-i18next';
import { useClassStore } from '../store/useClassStore';
import { normalizeLanguage } from '../i18n';
import api from '../lib/api';
// ClassSelectionModal removed — single family mode
import SettingsModal from '../components/SettingsModal';
import OnboardingWizard from '../components/OnboardingWizard';
import StudentActionModal from '../components/StudentActionModal';
import PetSelectionModal from '../components/PetSelectionModal';
import CosImage from '../components/CosImage';
import { playSound, setSoundEnabled, warmupSounds } from '../lib/sounds';

import {
  defaultPetLifeStateConfig,
  derivePetLifeInfo,
  getPetImage,
  getPetLifeStateStyle,
  getPetLifeStateText,
  getPetName,
  parsePetLifeStateConfig,
  type PetLifeInfo,
  type PetLifeState,
  type PetLifeStateConfig,
} from '../lib/petUtils';
import { usePetStore } from '../store/usePetStore';
import StoreModal from '../components/StoreModal';
import Toast from '../components/Toast';
import GrowthRecordsModal from '../components/GrowthRecordsModal';
import ToolsModal, { type TimerState } from '../components/ToolsModal';
import RenamePetModal from '../components/RenamePetModal';
import ReportsModal from '../components/ReportsModal';
import Portal from '../components/Portal';

interface Class {
  id: number;
  name: string;
  isSoundEnabled?: boolean;
  isAttenuationEnabled?: boolean;
  allowMultipleGroups?: boolean;
  lockScreenPassword?: string | null;
  students?: { id: number }[];
  studentCount?: number;
}

interface Student {
  id: number;
  name: string;
  studentNumber: string;
  currentPoints: number;
  petType: string | null;
  nickname?: string | null;
  badges?: number;
  unlockedPets?: string[];
  coins?: number;
  availablePoints?: number;
  groupIds?: number[];
  lastFeedTime?: string | null;
  lifeState?: PetLifeState | null;
  sleepTime?: string | null;
  // Mock fields for UI
  petLevel?: number;
  nextLevelPoints?: number;
  progress?: number;
  petLifeInfo?: PetLifeInfo;
}

export default function Home() {
  const LOCK_PASSWORD_LENGTH = 4;
  const { t, i18n } = useTranslation();
  const { currentClass, setCurrentClass } = useClassStore();
  const { petConfig, fetchPetConfig } = usePetStore();
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | 'all' | 'unassigned'>('all');
  const isSoundEnabled = currentClass?.isSoundEnabled ?? false;
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'normal' | 'compact'>(() => {
    try {
      const saved = localStorage.getItem('home-view-mode');
      if (saved === 'compact') return 'compact';
      return 'normal';
    } catch { return 'normal'; }
  });
  const [sortMode, setSortMode] = useState<'name_asc' | 'score_desc'>(() => {
    try {
      const saved = localStorage.getItem('home-sort-mode');
      if (saved === 'name_asc' || saved === 'score_desc') return saved;
      return 'score_desc';
    } catch {
      return 'score_desc';
    }
  });
  // isClassModalOpen removed — single family mode
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'account' | 'growth' | 'items' | 'students'>('account');
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [growthConfig, setGrowthConfig] = useState<Record<string, number>>({});
  const [petLifeConfig, setPetLifeConfig] = useState<PetLifeStateConfig | null>(null);

  const defaultGrowthConfig: Record<string, number> = {
    '0': 0,
    '1': 5,
    '2': 10,
    '3': 20,
    '4': 30,
    '5': 45,
    '6': 60,
    '7': 75,
    '8': 90,
    '9': 100,
  };
  const getBadgePointValue = (config: Record<string, number>) => {
    const v9 = Number.isFinite(config['9']) ? config['9'] : undefined;
    const v10 = Number.isFinite(config['10']) ? config['10'] : undefined;
    const candidate = Math.max(v9 ?? -Infinity, v10 ?? -Infinity);
    if (Number.isFinite(candidate) && candidate > 0) return candidate;
    const fallback = defaultGrowthConfig['9'];
    return Number.isFinite(fallback) && fallback > 0 ? fallback : 100;
  };
  const parseGrowthConfig = (value: unknown): Record<string, number> | null => {
    if (typeof value !== 'string') return null;
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const record: Record<string, number> = {};
        parsed.forEach((v, idx) => {
          const n = Number(v);
          if (Number.isFinite(n)) record[idx.toString()] = n;
        });
        if (Object.keys(record).length === 0) return null;
        record['0'] = 0;
        return record;
      }
      if (parsed && typeof parsed === 'object') {
        const record: Record<string, number> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          const n = Number(v);
          if (Number.isFinite(n)) record[k] = n;
        }
        if (Object.keys(record).length === 0) return null;
        if (!('0' in record)) record['0'] = 0;
        return record;
      }
      return null;
    } catch {
      return null;
    }
  };
  
  // Pet Selection Modal
  const [isPetModalOpen, setIsPetModalOpen] = useState(false);
  const [petSelectionStudent, setPetSelectionStudent] = useState<{ id: number, name: string } | null>(null);
  const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
  const [timerState, setTimerState] = useState<TimerState>({ timerMode: 'countdown', timeLeft: 0, initialTime: 0, isTimerRunning: false });
  const timerIntervalRef = useRef<number | null>(null);
  const isTimerMinimized = !isToolsModalOpen && timerState.isTimerRunning;
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [isGrowthRecordsModalOpen, setIsGrowthRecordsModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameStudent, setRenameStudent] = useState<Student | null>(null);
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [isScreenLocked, setIsScreenLocked] = useState(false);
  const [isLockPanelOpen, setIsLockPanelOpen] = useState(false);
  const [lockDialogMode, setLockDialogMode] = useState<'set' | 'confirm' | 'unlock' | 'verify'>('set');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [lockInput, setLockInput] = useState('');
  const [firstLockInput, setFirstLockInput] = useState('');
  const [lockError, setLockError] = useState<string | null>(null);
  const [isLockSubmitting, setIsLockSubmitting] = useState(false);
  const groupsDirtyRef = useRef(false);

  // Batch Mode State
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(new Set());

  // Feedback Modal State
  const [feedbackInfo, setFeedbackInfo] = useState<{
    type: 'points',
    studentName: string,
    points: number,
    isBatch: boolean,
    count?: number
  } | null>(null);
  
  // Toast State
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  // Level Up Modal State
  const [levelUpInfo, setLevelUpInfo] = useState<{ student: Student, oldLevel: number, newLevel: number } | null>(null);
  const levelUpTimeoutRef = useRef<number | null>(null);

  // Prestige Modal State
  const [prestigeInfo, setPrestigeInfo] = useState<{ student: Student, badges: number } | null>(null);
  // Full Level State (for showing the golden button)
  const [fullLevelStudent, setFullLevelStudent] = useState<{ id: number, name: string } | null>(null);
  const [isPrestigeCollecting, setIsPrestigeCollecting] = useState(false);

  // Loading State
  const [isLoading, setIsLoading] = useState(true);
  const [isClassDataLoading, setIsClassDataLoading] = useState(false);
  const isFetchingClassDataRef = useRef(false);

  // Timer sounds for minimized mode
  const playTimerTickSound = () => {
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch { /* ignore */ }
  };

  const playTimerEndSound = () => {
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.3);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + i * 0.15 + 0.3);
      });
    } catch { /* ignore */ }
  };

  // Keep timer ticking when minimized (modal closed but timer running)
  useEffect(() => {
    if (!isTimerMinimized) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }
    timerIntervalRef.current = window.setInterval(() => {
      setTimerState((prev) => {
        if (prev.timerMode === 'countdown') {
          const next = prev.timeLeft - 1;
          if (next > 0 && next <= 10) {
            playTimerTickSound();
          } else if (next <= 0) {
            playTimerEndSound();
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            return { ...prev, timeLeft: 0, isTimerRunning: false };
          }
          return { ...prev, timeLeft: next };
        } else {
          return { ...prev, timeLeft: prev.timeLeft + 1 };
        }
      });
    }, 1000);
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isTimerMinimized]);

  useEffect(() => {
    try {
      localStorage.setItem('home-sort-mode', sortMode);
    } catch {
      return;
    }
  }, [sortMode]);

  useEffect(() => {
    try { localStorage.setItem('home-view-mode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  useEffect(() => {
    fetchClasses();
    fetchPetConfig();
  }, []);

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const dayKey = `${y}-${m}-${d}`;

    const storedUsername = (() => {
      try {
        const raw = localStorage.getItem('user');
        if (!raw) return '';
        const parsed = JSON.parse(raw) as { username?: unknown };
        return typeof parsed.username === 'string' ? parsed.username : '';
      } catch {
        return '';
      }
    })();

    const dedupeKey = `active_ping:${storedUsername || 'unknown'}:${dayKey}`;
    try {
      if (localStorage.getItem(dedupeKey)) return;
    } catch {
      // ignore
    }

    void api
      .post('/events/active')
      .then(() => {
        try {
          localStorage.setItem(dedupeKey, '1');
        } catch {
          // ignore
        }
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    return () => {
      if (levelUpTimeoutRef.current) {
        clearTimeout(levelUpTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentClass?.id) {
      let isCancelled = false;
      isFetchingClassDataRef.current = true;
      setIsClassDataLoading(true);
      setSelectedGroupId('all');
      void (async () => {
        const loadedGrowthConfig = await fetchGrowthConfig(currentClass.id);
        await Promise.all([
          fetchPetLifeConfig(currentClass.id),
          fetchStudents(currentClass.id, loadedGrowthConfig),
          fetchGroups(currentClass.id),
        ]);
      })().finally(() => {
        isFetchingClassDataRef.current = false;
        if (!isCancelled) {
          setIsClassDataLoading(false);
        }
      });
      return () => {
        isCancelled = true;
      };
    }
    setIsClassDataLoading(false);
  }, [currentClass?.id]);

  useEffect(() => {
    if (currentClass?.id && !isFetchingClassDataRef.current) {
      fetchPetLifeConfig(currentClass.id);
    }
  }, [currentClass?.isAttenuationEnabled]);

  // Reset selectedGroupId if the group no longer exists
  useEffect(() => {
    if (typeof selectedGroupId === 'number') {
      const groupExists = groups.some(g => g.id === selectedGroupId);
      if (!groupExists) {
        setSelectedGroupId('all');
      }
    }
  }, [groups, selectedGroupId]);

  const fetchGroups = async (classId: number) => {
    try {
      const response = await api.get(`/groups?classId=${classId}`);
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups', error);
      setGroups([]);
    }
  };

  const fetchGrowthConfig = async (classId: number): Promise<Record<string, number>> => {
    try {
      const response = await api.get(`/settings/key/growth_config?classId=${classId}`);
      const config = parseGrowthConfig(response.data?.value);
      if (config) {
        setGrowthConfig(config);
        return config;
      } else {
        setGrowthConfig(defaultGrowthConfig);
        return defaultGrowthConfig;
      }
    } catch (error) {
      console.error('Failed to fetch growth config', error);
      setGrowthConfig(defaultGrowthConfig);
      return defaultGrowthConfig;
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      setIsLoading(false);
      setClasses(response.data);
      // If no class is selected (or persisted class not in list), select the first one
      if (response.data.length > 0) {
        const foundClass = currentClass 
          ? response.data.find((c: Class) => c.id === currentClass.id) 
          : null;
        
        if (foundClass) {
          const countOf = (c: Class | null) => {
            if (c && typeof c.studentCount === 'number') return c.studentCount;
            return c?.students?.length ?? 0;
          };
          // Ensure we have the latest data (e.g. updated student counts)
          // Only update if critical data changed to avoid unnecessary re-fetches
          if (!currentClass || 
              foundClass.name !== currentClass.name || 
              foundClass.isSoundEnabled !== currentClass.isSoundEnabled ||
              foundClass.isAttenuationEnabled !== currentClass.isAttenuationEnabled ||
              foundClass.lockScreenPassword !== currentClass.lockScreenPassword ||
              countOf(foundClass) !== countOf(currentClass)) {
            setCurrentClass(foundClass);
          }
        } else {
          setCurrentClass(response.data[0]);
        }
      } else {
        setCurrentClass(null);
        setStudents([]);
      }
    } catch (error) {
      console.error('Failed to fetch classes', error);
    }
  };

  const fetchPetLifeConfig = async (classId: number) => {
    if (currentClass?.isAttenuationEnabled === false) {
      setPetLifeConfig(defaultPetLifeStateConfig);
      return;
    }

    try {
      const response = await api.get(`/settings/key/pet_life_state_config?classId=${classId}`);
      // Use setPetLifeConfig directly with parsed value
      // If value is null/undefined/empty string, parsePetLifeStateConfig returns default (enabled=true)
      // But we want to handle the "loading" state separately or rely on what backend returns.
      // Actually parsePetLifeStateConfig returns default if invalid.
      // If backend returns 404 or empty, we get default.
      
      const parsed = parsePetLifeStateConfig(response.data?.value);
      setPetLifeConfig(parsed);
    } catch (error) {
      console.error('Failed to fetch pet life config', error);
      // On error, we might want to disable it to avoid showing wrong states?
      // Or fallback to default?
      // Let's fallback to default but maybe we should consider it not loaded?
      setPetLifeConfig(defaultPetLifeStateConfig);
    }
  };

  const calculateLevelInfo = (currentPoints: number, configOverride?: Record<string, number>) => {
    const config = configOverride && Object.keys(configOverride).length > 0
      ? configOverride
      : (Object.keys(growthConfig).length > 0 ? growthConfig : defaultGrowthConfig);
    let level = 1;
    const maxLevel = 10;
    
    // Find current level based on cumulative points
    for (let i = 1; i <= maxLevel; i++) {
      const requiredPoints = config[i.toString()];
      if (currentPoints >= requiredPoints) {
        level = i + 1;
      } else {
        break;
      }
    }
    
    // Cap level at maxLevel
    if (level > maxLevel) level = maxLevel;

    // Calculate points needed for next level
    // If level is max, progress is 100%
    if (level === maxLevel) {
      return {
        level,
        nextLevelPoints: 0,
        progress: 100
      };
    }

    const currentLevelThreshold = config[(level - 1).toString()] || 0;
    const nextLevelThreshold = config[level.toString()] || 100;
    
    const pointsSpan = nextLevelThreshold - currentLevelThreshold;
    const safeSpan = pointsSpan > 0 ? pointsSpan : 1;
    const effectivePoints = Math.max(currentPoints, currentLevelThreshold);

    const pointsToNextLevel = Math.max(0, nextLevelThreshold - effectivePoints);

    // Progress = (Effective Points - Previous Level Threshold) / (Next Level Threshold - Previous Level Threshold)
    const pointsInCurrentLevel = effectivePoints - currentLevelThreshold;
    const progress = Math.min(100, Math.max(0, (pointsInCurrentLevel / safeSpan) * 100));

    return {
      level,
      nextLevelPoints: pointsToNextLevel,
      progress
    };
  };

  const fetchStudents = async (classId: number, growthConfigOverride?: Record<string, number>) => {
    try {
      const response = await api.get(`/students?classId=${classId}`);
      const enrichedStudents = response.data.map((s: Student) => enrichStudent(s, growthConfigOverride));
      setStudents(enrichedStudents);

      // Update selectedStudent if it exists, to keep Modal data fresh
      if (selectedStudent) {
        const updated = enrichedStudents.find((s: Student) => s.id === selectedStudent.id);
        if (updated) setSelectedStudent(updated);
      }
      return enrichedStudents as Student[];
    } catch (error) {
      console.error('Failed to fetch students', error);
      return [] as Student[];
    }
  };

  const enrichStudent = (s: Student, growthConfigOverride?: Record<string, number>): Student => {
    const levelInfo = calculateLevelInfo(s.currentPoints, growthConfigOverride);
    const effectivePetLifeConfig = petLifeConfig || defaultPetLifeStateConfig;
    const forceSleeping = currentClass?.isAttenuationEnabled !== false && s.currentPoints < 0;
    const petLifeInfo = currentClass?.isAttenuationEnabled === false
      ? derivePetLifeInfo(undefined, effectivePetLifeConfig, 'normal')
      : forceSleeping
        ? derivePetLifeInfo(undefined, effectivePetLifeConfig, 'dead')
        : derivePetLifeInfo(s.lastFeedTime, effectivePetLifeConfig, s.lifeState ?? undefined);

    return {
      ...s,
      petLevel: levelInfo.level,
      nextLevelPoints: levelInfo.nextLevelPoints,
      progress: levelInfo.progress,
      petLifeInfo,
      lifeState: petLifeInfo.lifeState,
    };
  };

  useEffect(() => {
    if (!students.length) return;

    setStudents((prev) => prev.map((student) => enrichStudent(student)));
    if (selectedStudent) {
      setSelectedStudent((prev) => (prev ? enrichStudent(prev) : prev));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growthConfig, petLifeConfig, currentClass?.isAttenuationEnabled]);

  const getLevelColor = (level: number = 1) => {
    const colors = [
      { bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500' },     // Lv.1
      { bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500' },       // Lv.2
      { bg: 'bg-indigo-100', text: 'text-indigo-600', dot: 'bg-indigo-500' }, // Lv.3
      { bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500' }, // Lv.4
      { bg: 'bg-pink-100', text: 'text-pink-600', dot: 'bg-pink-500' },       // Lv.5
      { bg: 'bg-rose-100', text: 'text-rose-600', dot: 'bg-rose-500' },       // Lv.6
      { bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500' }, // Lv.7
      { bg: 'bg-amber-100', text: 'text-amber-600', dot: 'bg-amber-500' },    // Lv.8
      { bg: 'bg-yellow-100', text: 'text-yellow-600', dot: 'bg-yellow-500' }, // Lv.9
      { bg: 'bg-red-100', text: 'text-red-600', dot: 'bg-red-500' },          // Lv.10
    ];
    return colors[Math.min(Math.max(0, level - 1), colors.length - 1)];
  };

  const handlePrestigeCollect = async () => {
    if (!fullLevelStudent || !currentClass) return;
    if (isPrestigeCollecting) return;
    
    // Find current student data
    const student = students.find(s => s.id === fullLevelStudent.id);
    if (!student) return;

    try {
      setIsPrestigeCollecting(true);
      await api.post(`/students/${student.id}/prestige`);
      
      // Refresh data
      fetchStudents(currentClass.id);
      
      // Close Modal
      setPrestigeInfo(null);
      setFullLevelStudent(null);
    } catch (error) {
      console.error('Failed to prestige student', error);
    } finally {
      setIsPrestigeCollecting(false);
    }
  };

  const handleStudentUpdate = async (points: number) => {
    if (!currentClass) return;

    const targetStudentId = isBatchMode ? null : selectedStudent?.id ?? null;
    const studentBeforeUpdate = targetStudentId ? students.find(s => s.id === targetStudentId) : null;
    const oldLevel = studentBeforeUpdate ? calculateLevelInfo(studentBeforeUpdate.currentPoints).level : 1;

    // Check for level up immediately using local data
    let isLevelUp = false;
    let isLevelDown = false;
    if (!isBatchMode && studentBeforeUpdate && targetStudentId) {
      const projectedPoints = studentBeforeUpdate.currentPoints + points;
      const newLevel = calculateLevelInfo(projectedPoints).level;

      if (newLevel > oldLevel) {
        isLevelUp = true;
        if (newLevel === 10) {
          playSound('maxLevel');
        } else {
          playSound('levelUp');
        }

        setLevelUpInfo({
          student: { ...studentBeforeUpdate, currentPoints: projectedPoints, petLevel: newLevel },
          oldLevel,
          newLevel
        });
        if (levelUpTimeoutRef.current) clearTimeout(levelUpTimeoutRef.current);
        levelUpTimeoutRef.current = window.setTimeout(() => setLevelUpInfo(null), 4000);
      } else if (newLevel < oldLevel) {
        isLevelDown = true;
        playSound('levelDown');
      }
    }

    // Play points sound only if level didn't change
    if (!isLevelUp && !isLevelDown) {
      if (points > 0) {
        playSound('pointsUp');
      } else if (points < 0) {
        playSound('pointsDown');
      }
    }

    // Show feedback immediately if not level up
    if (!isLevelUp) {
      if (isBatchMode) {
        setFeedbackInfo({
          type: 'points',
          studentName: '',
          points,
          isBatch: true,
          count: selectedStudentIds.size
        });

        setIsBatchMode(false);
        setSelectedStudentIds(new Set());
        setIsActionModalOpen(false);
        setSelectedStudent(null);
      } else if (selectedStudent) {
        setFeedbackInfo({
          type: 'points',
          studentName: selectedStudent.name,
          points,
          isBatch: false
        });
      }

      if (isBatchMode || selectedStudent) {
        setTimeout(() => {
          setFeedbackInfo(null);
        }, 1500);
      }
    }

    // Fetch latest data in background to ensure consistency
    await fetchStudents(currentClass.id);
  };

  const toggleBatchMode = () => {
    setIsBatchMode(!isBatchMode);
    setSelectedStudentIds(new Set());
    setSelectedStudent(null);
  };

  const toggleSound = async () => {
    if (!currentClass) return;
    const newState = !isSoundEnabled;
    try {
      await api.put(`/classes/${currentClass.id}`, { isSoundEnabled: newState });
      setCurrentClass({ ...currentClass, isSoundEnabled: newState });
      setSoundEnabled(newState);
      setClasses(prev => prev.map(c => c.id === currentClass.id ? { ...c, isSoundEnabled: newState } : c));
    } catch (e) {
      console.error(e);
    }
  };

  const getStoredUsername = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      const parsed = JSON.parse(raw) as { username?: unknown };
      return typeof parsed.username === 'string' ? parsed.username : '';
    } catch {
      return '';
    }
  };

  const getScreenLockStorageKey = (classId: number) => {
    const storedUsername = getStoredUsername();
    return `screen_lock:${storedUsername || 'unknown'}:${classId}`;
  };

  const isScreenLockPersisted = (classId: number) => {
    try {
      return localStorage.getItem(getScreenLockStorageKey(classId)) === '1';
    } catch {
      return false;
    }
  };

  const persistScreenLock = (classId: number) => {
    try {
      localStorage.setItem(getScreenLockStorageKey(classId), '1');
    } catch {
      return;
    }
  };

  const clearPersistedScreenLock = (classId: number) => {
    try {
      localStorage.removeItem(getScreenLockStorageKey(classId));
    } catch {
      return;
    }
  };

  const resetLockDialog = () => {
    setIsLockPanelOpen(false);
    setLockDialogMode('set');
    setLockInput('');
    setFirstLockInput('');
    setLockError(null);
    setIsLockSubmitting(false);
    setIsChangingPassword(false);
  };

  const activateScreenLock = () => {
    setIsBatchMode(false);
    setSelectedStudentIds(new Set());
    setSelectedStudent(null);
    setIsActionModalOpen(false);
    setIsPetModalOpen(false);
    setIsRenameModalOpen(false);
    setIsToolsModalOpen(false);
    setIsSettingsModalOpen(false);
    setIsStoreModalOpen(false);
    setIsReportsModalOpen(false);
    setIsGrowthRecordsModalOpen(false);
    setIsSortModalOpen(false);
    if (currentClass?.id) {
      persistScreenLock(currentClass.id);
    }
    setIsScreenLocked(true);
    resetLockDialog();
  };

  const submitLockInput = async (value: string) => {
    if (!currentClass) return;
    if (isLockSubmitting) return;

    if (lockDialogMode === 'verify') {
      if (value === (currentClass.lockScreenPassword ?? '')) {
        setLockInput('');
        setLockError(null);
        setLockDialogMode('set');
        return;
      }
      setLockInput('');
      setLockError(t('home.lock.wrong'));
      return;
    }

    if (lockDialogMode === 'set') {
      setFirstLockInput(value);
      setLockInput('');
      setLockDialogMode('confirm');
      setLockError(null);
      return;
    }

    if (lockDialogMode === 'confirm') {
      if (value !== firstLockInput) {
        setLockInput('');
        setFirstLockInput('');
        setLockDialogMode('set');
        setLockError(t('home.lock.mismatch'));
        return;
      }

      try {
        setIsLockSubmitting(true);
        await api.put(`/classes/${currentClass.id}`, { lockScreenPassword: value });
        const updatedClass = { ...currentClass, lockScreenPassword: value };
        setCurrentClass(updatedClass);
        setClasses((prev) => prev.map((c) => (c.id === currentClass.id ? { ...c, lockScreenPassword: value } : c)));
        if (isChangingPassword) {
          setToastMessage(t('home.lock.changeSuccess'));
          resetLockDialog();
        } else {
          setToastMessage(t('home.lock.saveSuccess'));
          activateScreenLock();
        }
      } catch (error) {
        setLockInput('');
        setFirstLockInput('');
        setLockDialogMode('set');
        setLockError(t('home.lock.saveFailed'));
        console.error(error);
      } finally {
        setIsLockSubmitting(false);
      }
      return;
    }

    if (value === (currentClass.lockScreenPassword ?? '')) {
      clearPersistedScreenLock(currentClass.id);
      setIsScreenLocked(false);
      resetLockDialog();
      return;
    }
    setLockInput('');
    setLockError(t('home.lock.wrong'));
  };

  const handleLockDigit = (digit: string) => {
    if (isLockSubmitting) return;
    if (lockInput.length >= LOCK_PASSWORD_LENGTH) return;
    const next = `${lockInput}${digit}`;
    setLockInput(next);
    if (next.length === LOCK_PASSWORD_LENGTH) {
      void submitLockInput(next);
    }
  };

  const handleLockDelete = () => {
    if (isLockSubmitting) return;
    setLockError(null);
    setLockInput((prev) => prev.slice(0, -1));
  };

  const handleOpenLockDialog = () => {
    if (!currentClass) return;
    if (!currentClass.lockScreenPassword) {
      setLockDialogMode('set');
      setLockInput('');
      setFirstLockInput('');
      setLockError(null);
      setIsLockPanelOpen(true);
      return;
    }
    activateScreenLock();
  };

  const handleOpenUnlockDialog = () => {
    if (!currentClass || !isScreenLocked) return;
    setLockDialogMode('unlock');
    setLockInput('');
    setLockError(null);
    setIsLockPanelOpen(true);
  };

  const handleLockCancel = () => {
    if (isLockSubmitting) return;
    resetLockDialog();
  };

  useEffect(() => {
    resetLockDialog();
    if (!currentClass?.id) {
      setIsScreenLocked(false);
      return;
    }
    if (!currentClass.lockScreenPassword) {
      clearPersistedScreenLock(currentClass.id);
      setIsScreenLocked(false);
      return;
    }
    if (isScreenLockPersisted(currentClass.id)) {
      activateScreenLock();
      return;
    }
    setIsScreenLocked(false);
  }, [currentClass?.id, currentClass?.lockScreenPassword]);

  useEffect(() => {
    setSoundEnabled(currentClass?.isSoundEnabled ?? false);
    if (currentClass?.isSoundEnabled) {
      const schedule = (cb: () => void) => {
        const ric = (window as unknown as { requestIdleCallback?: (fn: () => void) => number }).requestIdleCallback;
        if (typeof ric === 'function') {
          ric(cb);
          return;
        }
        window.setTimeout(cb, 300);
      };
      schedule(() => {
        void warmupSounds(['pointsUp', 'pointsDown', 'levelUp', 'levelDown', 'maxLevel']);
      });
    }
  }, [currentClass?.id, currentClass?.isSoundEnabled]);

  const handleBatchSelect = (id: number) => {
    const newSelected = new Set(selectedStudentIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedStudentIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedStudentIds.size === displayStudents.length) {
       setSelectedStudentIds(new Set());
    } else {
       setSelectedStudentIds(new Set(displayStudents.map(s => s.id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedStudentIds(new Set());
  };

  const handleBatchFeed = () => {
     if (selectedStudentIds.size === 0) return;
     setIsActionModalOpen(true);
  };

  const handleStudentClick = (student: Student) => {
    // If no pet selected, open pet selection modal
    if (!student.petType) {
      setPetSelectionStudent(student);
      setIsPetModalOpen(true);
    } else {
      // If pet selected, open action modal
      setSelectedStudent(student);
      setIsActionModalOpen(true);
    }
  };

  const handlePetClick = (e: React.MouseEvent, student: Student) => {
    e.stopPropagation();
    
    // Check if student is full level (Lv.10 and progress 100%)
    // Since we don't store "isFullLevel" directly, calculate it
    const isFullLevel = student.petLevel === 10 && student.progress === 100;
    
    if (isFullLevel) {
       setFullLevelStudent({ id: student.id, name: student.name });
       setPrestigeInfo({ student, badges: student.badges || 0 });
       return;
    }

    // Allow pet selection if no pet selected
    // Note: If points are 0, user must click the pencil icon to re-select pet
    if (!student.petType) {
      setPetSelectionStudent(student);
      setIsPetModalOpen(true);
    } else {
      // If pet selected, open action modal (even if points are 0)
      setSelectedStudent(student);
      setIsActionModalOpen(true);
    }
  };

  const handleRenameClick = (e: React.MouseEvent, student: Student) => {
    e.stopPropagation();
    if (!student.petType) return;
    setRenameStudent(student);
    setIsRenameModalOpen(true);
  };

  const filteredStudents = students.filter(s => {
    const query = searchQuery.trim();
    if (selectedGroupId === 'unassigned') {
      if (s.groupIds && s.groupIds.length > 0) return false;
    } else if (selectedGroupId !== 'all' && !(s.groupIds || []).includes(selectedGroupId as number)) {
      return false;
    }
    if (!query) return true;
    return s.name.toLowerCase().includes(query.toLowerCase()) || 
           s.studentNumber?.includes(query) ||
           !!match(s.name, query);
  });

  const getStudentScore = (student: Student) => {
    const config = Object.keys(growthConfig).length > 0 ? growthConfig : defaultGrowthConfig;
    const badgePointValue = getBadgePointValue(config);
    return (student.currentPoints || 0) + (student.badges || 0) * badgePointValue;
  };

  const displayStudents = [...filteredStudents].sort((a, b) => {
    const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    if (sortMode === 'name_asc') {
      const byName = a.name.localeCompare(b.name, activeLanguage, { sensitivity: 'base', numeric: true });
      if (byName !== 0) return byName;
      return a.id - b.id;
    }

    const byScore = getStudentScore(b) - getStudentScore(a);
    if (byScore !== 0) return byScore;
    const byName = a.name.localeCompare(b.name, activeLanguage, { sensitivity: 'base', numeric: true });
    if (byName !== 0) return byName;
    return a.id - b.id;
  });

  const lockPanelTitle = lockDialogMode === 'verify'
    ? t('home.lock.verifyTitle')
    : lockDialogMode === 'set'
      ? t('home.lock.setTitle')
      : lockDialogMode === 'confirm'
        ? t('home.lock.confirmTitle')
        : t('home.lock.unlockTitle');

  const lockPanelHint = lockDialogMode === 'verify'
    ? t('home.lock.verifyHint')
    : lockDialogMode === 'unlock'
      ? t('home.lock.unlockHint')
      : lockDialogMode === 'confirm'
        ? t('home.lock.confirmHint')
      : t('home.lock.setHint');

  return (
    <div className="min-h-screen p-4 relative">
      {/* 柔和暖色背景：奶油底色 + 角落粉/蜜桃光晕，呼应 onboarding wizard 氛围 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 40% at 8% 4%, rgba(255, 184, 213, 0.28) 0%, rgba(255, 184, 213, 0) 70%), radial-gradient(50% 35% at 96% 0%, rgba(255, 215, 127, 0.20) 0%, rgba(255, 215, 127, 0) 70%), linear-gradient(180deg, #fefcfb 0%, #fdfbfb 100%)',
        }}
      />
      {/* Top Navigation Bar — 仅在已添加孩子后显示，onboarding 阶段全屏让位给引导卡片 */}
      {students.length > 0 && (
      <header className={`bg-white/90 backdrop-blur-md rounded-clay-lg shadow-soft-lg px-3 md:px-5 py-2.5 md:py-3 flex flex-wrap lg:flex-nowrap items-center justify-between gap-y-2 gap-x-4 md:gap-x-6 mb-6 mx-auto max-w-7xl border border-pink-50 sticky top-[calc(0.5rem+env(safe-area-inset-top))] min-w-0 transition-all duration-300 ${isScreenLocked ? 'z-[9999] pointer-events-none' : 'z-50'}`}>
        {/* Left: Class Selector */}
        <div className="flex items-center gap-2 md:gap-3 min-w-[100px] flex-shrink-0">
          <button className="w-7 h-7 md:w-9 md:h-9 rounded-full border-2 border-pink-400 flex items-center justify-center text-pink-500 hover:bg-pink-50 transition-colors shrink-0" onClick={() => setIsGrowthRecordsModalOpen(true)}>
            <span className="text-base md:text-lg font-bold">☁️</span>
          </button>
          
          <div className="flex min-w-0 items-center gap-2">
            <span className="font-bold text-gray-700 text-xs md:text-base max-w-[150px] md:max-w-[200px] truncate min-w-0">
              {currentClass?.name || t('home.selectClass')}
            </span>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <button 
              onClick={toggleBatchMode}
              className={`font-bold text-xs flex items-center gap-1 transition-all px-2 md:px-3 py-1 rounded-full shrink-0 ${
                isBatchMode 
                  ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-md shadow-blue-200' 
                  : 'bg-pink-50 text-pink-600 hover:bg-pink-100 hover:text-pink-700'
              }`}
            >
              {isBatchMode ? (
                <>
                  <X className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('home.batch.exit')}</span>
                </>
              ) : (
                <>
                  <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('home.batch.enter')}</span>
                </>
              )}
            </button>
            <button
              onClick={toggleSound}
              className={`rounded-full flex items-center justify-center transition-colors shrink-0 ${
                isSoundEnabled 
                  ? 'bg-pink-100 text-pink-500 hover:bg-pink-200' 
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              } w-7 h-7 sm:w-auto sm:h-auto sm:px-3 sm:py-1 gap-1`}
              title={isSoundEnabled ? "关闭音效" : "开启音效"}
            >
              {isSoundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <span className="hidden sm:inline font-bold text-xs">
                {isSoundEnabled ? '音效: 开' : '音效: 关'}
              </span>
            </button>
          </div>
        </div>

        {/* Center: Search — 仅在已添加孩子后显示 */}
        {students.length > 0 && (
          <div className="flex-1 max-w-md min-w-[150px] mx-2 md:mx-6 hidden sm:block">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-pink-400 transition-colors" />
              <input
                type="text"
                placeholder={t('home.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-50 border border-transparent focus:bg-white focus:border-pink-200 rounded-full py-1.5 pl-9 pr-4 text-sm outline-none transition-all placeholder-gray-400 text-gray-700"
              />
            </div>
          </div>
        )}

        {/* Right: Menu */}
        <div className="flex items-center gap-1 md:gap-3 lg:gap-4 flex-shrink-0">
          {/* View Mode Toggle */}
          {/* <button
            className="flex items-center justify-center bg-white border border-pink-50 shadow-sm rounded-full p-1.5 text-gray-600 hover:text-pink-500 transition-colors shrink-0"
            onClick={() => setViewMode(v => v === 'normal' ? 'compact' : 'normal')}
            title={viewMode === 'normal' ? t('home.viewMode.compact', '小图模式') : t('home.viewMode.normal', '普通模式')}
          >
            {viewMode === 'normal' ? <Grid3X3 className="w-3.5 h-3.5" /> : <LayoutGrid className="w-3.5 h-3.5" />}
          </button> */}
          {/* Mobile Sort Button */}
          {/* <button
            className="sm:hidden flex items-center justify-center bg-white border border-pink-50 shadow-sm rounded-full p-1.5 text-gray-600 hover:text-pink-500 transition-colors shrink-0"
            onClick={() => setIsSortModalOpen(true)}
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
          </button> */}
          {/* <button
            onClick={isScreenLocked ? handleOpenUnlockDialog : handleOpenLockDialog}
            className={`flex items-center gap-1.5 bg-white border border-pink-50 shadow-sm rounded-full px-2.5 h-8 text-xs font-bold text-gray-700 hover:text-pink-500 transition-colors shrink-0 ${isScreenLocked ? 'pointer-events-auto' : ''}`}
          >
            {isScreenLocked ? <Lock className="w-3.5 h-3.5 text-orange-500" /> : <LockOpen className="w-3.5 h-3.5" />}
            <span className={`hidden sm:inline ${isScreenLocked ? 'text-orange-500' : ''}`}>
              {isScreenLocked ? t('home.lock.unlockButton') : t('home.lock.button')}
            </span>
          </button> */}
          {/* <div className="hidden sm:flex items-center gap-1.5 bg-white border border-pink-50 shadow-sm rounded-full px-2.5 h-8 shrink-0 cursor-pointer">
            <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
            <div className="relative">
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as 'name_asc' | 'score_desc')}
                className="appearance-none bg-transparent outline-none text-xs font-bold text-gray-700 leading-none pr-4 cursor-pointer"
              >
                <option value="score_desc">{t('home.sort.score')}</option>
                <option value="name_asc">{t('home.sort.name')}</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>
          </div> */}
          <button onClick={() => setIsStoreModalOpen(true)} className="flex items-center gap-1 text-gray-600 hover:text-pink-500 font-medium transition-colors p-1.5 md:p-0">
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden lg:inline text-sm">{t('home.nav.store')}</span>
          </button>
          <button onClick={() => setIsReportsModalOpen(true)} className="flex items-center gap-1 text-gray-600 hover:text-pink-500 font-medium transition-colors p-1.5 md:p-0">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden lg:inline text-sm">{t('home.nav.reports')}</span>
          </button>
          <button onClick={() => {
            setSettingsInitialTab('students');
            setIsSettingsModalOpen(true);
          }} className="flex items-center gap-1 text-gray-600 hover:text-pink-500 font-medium transition-colors p-1.5 md:p-0">
            <Settings className="w-4 h-4" />
            <span className="hidden lg:inline text-sm">{t('home.nav.settings')}</span>
          </button>
          <button onClick={() => setIsToolsModalOpen(true)} className="flex items-center gap-1 text-gray-600 hover:text-pink-500 font-medium transition-colors p-1.5 md:p-0">
            <Wrench className="w-4 h-4" />
            <span className="hidden lg:inline text-sm">{t('home.nav.tools')}</span>
          </button>
        </div>
      </header>
      )}

      {/* Mobile Search Bar (Visible only on small screens) — 仅在已添加孩子后显示 */}
      {students.length > 0 && (
        <div className="sm:hidden mb-6 px-2">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-pink-400 transition-colors" />
            <input
              type="text"
              placeholder={t('home.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-pink-50 shadow-sm focus:border-pink-200 rounded-full py-2.5 pl-12 pr-4 outline-none transition-all placeholder-gray-400 text-gray-700"
            />
          </div>
        </div>
      )}

      {/* Batch Mode Banner */}
      {isBatchMode && (
        <div className="max-w-md mx-auto mb-8 -mt-4 animate-in slide-in-from-top-4 duration-300 relative z-40">
          <div className="bg-blue-500 text-white px-6 py-3 rounded-full shadow-lg shadow-blue-200 flex items-center justify-center gap-2 font-bold text-sm">
            <Users className="w-4 h-4" />
            {t('home.batch.hint')}
          </div>
        </div>
      )}

      {/* Main Content: Student Cards Grid */}
      <main className="max-w-7xl mx-auto mb-24">
        {isLoading || (classes.length > 0 && !!currentClass?.id && isClassDataLoading) ? (
          <div className="flex justify-center items-center min-h-[60vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        ) : classes.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mb-6">
              <Plus className="w-10 h-10 text-indigo-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('home.empty.noStudents.title')}</h2>
            <p className="text-gray-400 mb-8 max-w-sm">
              {t('home.empty.noStudents.desc')}
            </p>
            <button
              onClick={() => {
                setSettingsInitialTab('students');
                setIsSettingsModalOpen(true);
              }}
              className="px-8 py-3 bg-pink-500 text-white font-bold rounded-2xl shadow-lg shadow-pink-200 hover:bg-pink-600 hover:shadow-pink-300 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('home.empty.noStudents.action')}
            </button>
          </div>
        ) : students.length === 0 && currentClass ? (
          <OnboardingWizard
            classId={currentClass.id}
            onComplete={async (newStudentId) => {
              await fetchStudents(currentClass.id);
              const newStudent = { id: newStudentId, name: '' };
              setPetSelectionStudent(newStudent);
              setIsPetModalOpen(true);
            }}
          />
        ) : (
          <div className={`flex flex-wrap ${viewMode === 'compact' ? '-mx-1' : '-mx-2.5 sm:-mx-3'}`}>
            {displayStudents.map((student) => {
          const forceSleeping = currentClass?.isAttenuationEnabled !== false && student.currentPoints < 0;
          const hidePetStatus = !!student.petType && student.currentPoints <= 0 && !forceSleeping;
          const isSelected = selectedStudentIds.has(student.id);

          if (viewMode === 'compact') {
            return (
              <div key={student.id} className="w-1/4 sm:w-1/5 md:w-1/6 lg:w-[12.5%] xl:w-[10%] px-1 mb-2">
                <div
                  onClick={() => isBatchMode ? handleBatchSelect(student.id) : handleStudentClick(student)}
                  className={`
                    relative rounded-2xl p-1.5 cursor-pointer group transition-all duration-200 ease-out
                    active:scale-[0.97]
                    ${isBatchMode && isSelected
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-white border border-transparent shadow-clay-sm hover:shadow-clay hover:-translate-y-0.5'
                    }
                    ${student.petLifeInfo?.lifeState === 'dead' && currentClass?.isAttenuationEnabled !== false ? 'grayscale' : ''}
                  `}
                >
                  {isBatchMode && (
                    <div className={`absolute top-1 left-1 z-20 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-200'}`}>
                      {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                  )}
                  <div className="flex justify-center mb-1 relative">
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden relative ${student.petType && student.petLifeInfo && !hidePetStatus ? getPetLifeStateStyle(student.petLifeInfo.lifeState).ring : ''}`}>
                      {student.petType ? (
                        <>
                          <CosImage
                            src={getPetImage(student.petType, student.petLevel, petConfig)}
                            alt="Pet"
                            className={`w-full h-full object-contain dragon-animate ${student.petLifeInfo && !hidePetStatus ? getPetLifeStateStyle(student.petLifeInfo.lifeState).filter : ''}`}
                          />
                          {student.petLifeInfo?.lifeState === 'dead' && !hidePetStatus && (
                            <div className="absolute inset-x-0 bottom-0 bg-gray-500/85 text-white text-[8px] font-bold py-0.5 text-center">
                              😴
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-indigo-50 to-purple-50">
                          <div className="w-8 h-10 bg-white rounded-[50%_50%_50%_50%_/_60%_60%_40%_40%] shadow-inner flex items-center justify-center">
                            <span className="text-sm font-black text-indigo-200">?</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-[11px] sm:text-xs font-bold text-gray-800 truncate leading-tight">{student.name}</h3>
                  </div>
                  {student.petLevel === 10 && student.progress === 100 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePetClick(e, student); }}
                      className="w-full mt-1 py-0.5 bg-gradient-to-r from-yellow-300 to-orange-300 hover:from-yellow-400 hover:to-orange-400 text-white font-black text-[10px] rounded-lg shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-0.5"
                    >
                      <span className="animate-pulse">✨</span> {t('home.summon')}
                    </button>
                  )}
                </div>
              </div>
            );
          }

          return (
          <div key={student.id} className="w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 xl:w-1/6 px-2.5 mb-5 sm:px-3 sm:mb-6">
            <div
              onClick={() => isBatchMode ? handleBatchSelect(student.id) : handleStudentClick(student)}
              className={`
                h-full relative rounded-clay p-3 cursor-pointer group transition-all duration-200 ease-out
                active:scale-[0.98]
                ${isBatchMode && isSelected
                  ? 'bg-blue-50 border-2 border-blue-500 shadow-clay'
                  : 'bg-white border border-transparent shadow-clay hover:shadow-clay-lg hover:-translate-y-0.5'
                }
                ${student.petLifeInfo?.lifeState === 'dead' && currentClass?.isAttenuationEnabled !== false ? 'grayscale' : ''}
              `}
            >
            {/* Batch Mode Checkbox */}
            {isBatchMode && (
              <div className={`
                absolute top-2 left-2 z-20 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                ${isSelected 
                  ? 'bg-blue-500 border-blue-500' 
                  : 'bg-white border-gray-200 group-hover:border-blue-300'
                }
              `}>
                {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
              </div>
            )}
            
            {/* Top Row: Level & Action */}
            <div className="flex justify-between items-center mb-1 min-h-[20px]">
              <span className={`${getLevelColor(student.petLevel).bg} ${getLevelColor(student.petLevel).text} px-1.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1`}>
                <div className={`w-1 h-1 rounded-full ${getLevelColor(student.petLevel).dot}`}></div>
                Lv.{student.petLevel}
              </span>

              {currentClass?.isAttenuationEnabled !== false && student.petType && student.petLifeInfo && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${getPetLifeStateStyle(student.petLifeInfo.lifeState).badge}`}>
                  {getPetLifeStateText(student.petLifeInfo.lifeState, petLifeConfig)}
                </span>
              )}
            </div>

            {/* Pet Image */}
            <div className="flex justify-center mb-2 relative">
              <div 
                className={`w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden relative group/pet cursor-pointer ${student.petType && student.petLifeInfo && !hidePetStatus ? getPetLifeStateStyle(student.petLifeInfo.lifeState).ring : ''}`}
                onClick={(e) => {
                  if (isBatchMode) {
                    e.stopPropagation();
                    handleBatchSelect(student.id);
                  } else {
                    handlePetClick(e, student);
                  }
                }}
              >
                {student.petType ? (
                  <>
                    <CosImage 
                      src={getPetImage(student.petType, student.petLevel, petConfig)} 
                      alt="Pet" 
                      className={`w-full h-full object-contain transition-transform duration-500 group-hover/pet:scale-110 dragon-animate ${student.petLifeInfo && !hidePetStatus ? getPetLifeStateStyle(student.petLifeInfo.lifeState).filter : ''}`}
                    />
                    {student.petLifeInfo?.lifeState === 'dead' && !hidePetStatus && (
                      <div className="absolute inset-x-2 bottom-2 rounded-full bg-gray-500/85 text-white text-[10px] font-bold py-1 text-center shadow-lg">
                        😴 {getPetLifeStateText('dead', petLifeConfig)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-indigo-50 to-purple-50 text-indigo-300 relative overflow-hidden">
                    {/* Egg Shape */}
                    <div className="w-16 h-20 bg-white rounded-[50%_50%_50%_50%_/_60%_60%_40%_40%] shadow-inner flex items-center justify-center relative z-10 group-hover/pet:scale-110 transition-transform duration-300">
                      <div className="text-2xl font-black text-indigo-200">?</div>
                      {/* Decorative dots */}
                      <div className="absolute top-3 left-3 w-1.5 h-1.5 bg-indigo-100 rounded-full"></div>
                      <div className="absolute bottom-5 right-4 w-1 h-1 bg-indigo-100 rounded-full"></div>
                    </div>
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-indigo-500/10 backdrop-blur-[1px] flex items-end justify-center pb-3 opacity-0 group-hover/pet:opacity-100 transition-opacity z-20">
                      <span className="bg-white text-indigo-600 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                        {t('home.student.adopt')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Name & Type */}
            <div className="flex justify-between items-end mb-1.5 px-0.5 gap-1.5">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 truncate leading-tight">{student.name}</h3>
              <span 
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md transition-colors flex items-center gap-0.5 whitespace-nowrap ${
                  student.petType 
                    ? 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200 cursor-pointer border border-indigo-200' 
                    : 'text-gray-400 bg-gray-50'
                }`}
                onClick={(e) => handleRenameClick(e, student)}
              >
                {student.petType && <Cat className="w-2.5 h-2.5 shrink-0" />}
                <span className="truncate max-w-[3.5em] sm:max-w-[4.5em]">
                  {(() => {
                    if (!student.petType) return t('home.student.mysteryEgg');
                    return student.nickname || getPetName(student.petType);
                  })()}
                </span>
              </span>
            </div>

            {/* {student.petType && student.petLifeInfo && (
              <div className="mb-2 space-y-1 rounded-xl bg-slate-50 px-2 py-2 border border-slate-100">
                <div className="flex items-center justify-between gap-2 text-[10px] sm:text-xs">
                  <span className="text-slate-500 font-medium">{t('home.student.daysWithoutFeed')}</span>
                  <span className="font-bold text-slate-700">{t('home.student.daysCount', { count: student.petLifeInfo.daysWithoutFeed })}</span>
                </div>
                <div className="text-[10px] sm:text-xs text-slate-500 leading-relaxed">
                  {student.petLifeInfo.lifeState === 'dead'
                    ? t('home.student.sleepHint')
                    : student.petLifeInfo.daysToNextState > 0
                      ? t('home.student.nextStateIn', { count: student.petLifeInfo.daysToNextState })
                      : getPetLifeStateHint(student.petLifeInfo.lifeState)}
                </div>
                <div className="text-[10px] sm:text-xs text-violet-500 font-medium">
                  {student.petLifeInfo.lifeState === 'dead'
                    ? t('home.student.waitingWakeUp')
                    : t('home.student.sleepIn', { count: student.petLifeInfo.daysToSleep })}
                </div>
              </div>
            )} */}

            {/* Progress Bar */}
            <div className="mb-2">
              <div className="flex justify-between text-[10px] mb-1">
                <span className="text-gray-400 font-medium">{student.petType ? t('home.student.progress') : t('home.student.hatching')}</span>
                {student.petLevel === 10 && student.progress === 100 ? (
                   <span className="text-orange-500 font-bold flex items-center gap-0.5">
                     {t('home.student.maxLevel')}
                   </span>
                ) : student.petType ? (
                  <span className="text-blue-500 font-bold flex items-center gap-0.5">
                    {t('home.student.remaining', { count: student.nextLevelPoints })} <Fish className="w-2.5 h-2.5 fill-blue-500" />
                  </span>
                ) : (
                  <span className="text-indigo-400 font-bold cursor-pointer hover:underline" onClick={(e) => handlePetClick(e, student)}>
                    {t('home.student.adopt')}
                  </span>
                )}
              </div>
              
              {student.petLevel === 10 && student.progress === 100 ? (
                 <div className="h-8 w-full mt-1.5 relative">
                   {/* Golden Progress Bar — 加粗到 2.5 */}
                   <div className="h-2.5 rounded-full overflow-hidden mb-2 bg-gray-100">
                     <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full w-full relative overflow-hidden">
                       <div className="absolute inset-0 bg-white/30 animate-[shimmer_2s_infinite] skew-x-12"></div>
                       <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-[8px]">✨</div>
                     </div>
                   </div>
                 </div>
              ) : (
                <div className="h-2.5 rounded-full overflow-hidden bg-gray-100">
                  <div
                    className={`h-full rounded-full ${
                      student.petType
                        ? 'bg-gradient-to-r from-pink-400 via-rose-400 to-orange-400'
                        : 'bg-gray-200'
                    }`}
                    style={{ width: student.petType ? `${student.progress}%` : '0%' }}
                  ></div>
                </div>
              )}
            </div>

            {/* Summon Button (Only visible when Max Level) */}
            {student.petLevel === 10 && student.progress === 100 && (
              <div className="mb-2">
                <button 
                  onClick={(e) => handlePetClick(e, student)}
                  className="w-full py-1.5 bg-gradient-to-r from-yellow-300 to-orange-300 hover:from-yellow-400 hover:to-orange-400 text-white font-black text-xs rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap"
                >
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <span className="animate-pulse">✨</span> {t('home.summon')}
                </button>
              </div>
            )}
            
            {/* Bottom Stats — 统一灰色背景块，让三个数字看起来是一组状态指示器 */}
            <div className="mt-2 flex justify-between items-center bg-gray-50/70 rounded-xl px-2.5 py-1.5">
              <div className="flex items-center gap-1 text-gray-700 font-bold text-xs">
                <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center">
                  <Fish className="w-2.5 h-2.5 text-orange-500 fill-orange-500" />
                </div>
                {student.currentPoints}
              </div>

              <div className="flex gap-2">
                <div className="flex items-center gap-0.5">
                  <Coins className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  <span className="text-[10px] font-bold text-gray-600">{student.availablePoints ?? 0}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <Award className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  <span className="text-[10px] font-bold text-gray-600">{student.badges || 0}</span>
                </div>
              </div>
            </div>

          </div>
          </div>
        )})}
          </div>
        )}
      </main>

      {isScreenLocked && (
        <Portal>
          <div className="fixed inset-0 z-[9998] touch-pan-y" />
        </Portal>
      )}

      {isLockPanelOpen && (
        <Portal>
          <div className="fixed bottom-6 right-6 z-[10000] w-[320px] max-w-[calc(100vw-1.5rem)] rounded-[2rem] bg-white p-5 shadow-[0_12px_40px_rgba(0,0,0,0.16)] border border-gray-100">
            <div className="mb-4 flex flex-col items-center text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-orange-500">
                {lockDialogMode === 'unlock' ? <LockOpen className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
              </div>
              <h3 className="text-2xl font-black text-gray-800">{lockPanelTitle}</h3>
              <p className="mt-1 text-xs font-medium text-gray-400">{lockPanelHint}</p>
            </div>

            <div className="mb-4 flex justify-center gap-3">
              {Array.from({ length: LOCK_PASSWORD_LENGTH }).map((_, index) => (
                <span
                  key={index}
                  className={`h-3.5 w-3.5 rounded-full ${index < lockInput.length ? 'bg-indigo-500' : 'bg-gray-200'}`}
                />
              ))}
            </div>

            {lockError && (
              <p className="mb-3 text-center text-xs font-bold text-rose-500">{lockError}</p>
            )}

            <div className="grid grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                <button
                  key={digit}
                  onClick={() => handleLockDigit(digit)}
                  disabled={isLockSubmitting}
                  className="h-12 rounded-xl text-3xl font-light text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                >
                  {digit}
                </button>
              ))}
              <button
                onClick={handleLockCancel}
                disabled={isLockSubmitting}
                className="h-12 rounded-xl text-base font-bold text-gray-300 hover:bg-gray-50 disabled:opacity-60"
              >
                {t('home.lock.cancel')}
              </button>
              <button
                onClick={() => handleLockDigit('0')}
                disabled={isLockSubmitting}
                className="h-12 rounded-xl text-3xl font-light text-gray-800 hover:bg-gray-50 disabled:opacity-60"
              >
                0
              </button>
              <button
                onClick={handleLockDelete}
                disabled={isLockSubmitting || lockInput.length === 0}
                className="h-12 rounded-xl flex items-center justify-center text-gray-400 hover:bg-gray-50 disabled:opacity-60"
              >
                <Delete className="w-5 h-5" />
              </button>
            </div>
            {lockDialogMode === 'unlock' && (
              <button
                onClick={() => {
                  setLockInput('');
                  setLockError(null);
                  setIsChangingPassword(true);
                  setLockDialogMode('verify');
                }}
                className="mt-3 w-full text-center text-xs font-medium text-gray-400 hover:text-indigo-500 transition-colors"
              >
                {t('home.lock.changePassword')}
              </button>
            )}
          </div>
        </Portal>
      )}

      {isBatchMode && (
        <Portal>
          <div className="fixed bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 bg-white/95 backdrop-blur-md border border-gray-200 p-3 sm:p-4 shadow-[0_-4px_30px_rgb(0,0,0,0.1)] z-[300] rounded-2xl" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="max-w-7xl mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                <span className="font-bold text-gray-700 whitespace-nowrap text-sm sm:text-base">
                  {t('home.batch.selectedPrefix')}{' '}
                  <span className="text-blue-500 text-lg sm:text-xl">{selectedStudentIds.size}</span>{' '}
                  {t('home.batch.selectedSuffix')}
                </span>
                <button
                  onClick={handleSelectAll}
                  className="text-gray-500 hover:text-gray-700 font-medium transition-colors whitespace-nowrap text-sm sm:text-base"
                >
                  {selectedStudentIds.size === displayStudents.length ? t('home.batch.deselectAll') : t('home.batch.selectAll')}
                </button>
                <button
                  onClick={handleClearSelection}
                  className="text-gray-500 hover:text-gray-700 font-medium transition-colors whitespace-nowrap text-sm sm:text-base"
                >
                  {t('home.batch.clear')}
                </button>
              </div>

              <button
                onClick={handleBatchFeed}
                disabled={selectedStudentIds.size === 0}
                className={`
                  w-full sm:w-auto px-4 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base
                  ${selectedStudentIds.size > 0
                    ? 'bg-blue-500 hover:bg-blue-600 shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-0.5'
                    : 'bg-gray-300 cursor-not-allowed'
                  }
                `}
              >
                <Users className="w-5 h-5" />
                {t('home.batch.feed')}
              </button>
            </div>
          </div>
        </Portal>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}

      <SettingsModal
        key={isSettingsModalOpen ? "open" : "closed"}
        isOpen={isSettingsModalOpen}
        onClose={() => {
          setIsSettingsModalOpen(false);
          if (groupsDirtyRef.current && currentClass) {
            fetchStudents(currentClass.id);
            fetchGroups(currentClass.id);
            groupsDirtyRef.current = false;
          }
        }}
        onUpdate={() => {
          fetchClasses();
          if (currentClass) fetchStudents(currentClass.id);
        }}
        onClassesUpdate={fetchClasses}
        onGrowthUpdate={() => {
          if (currentClass) {
            fetchGrowthConfig(currentClass.id);
            fetchPetLifeConfig(currentClass.id);
          }
        }}
        initialTab={settingsInitialTab}
        classes={classes}
      />

      <StudentActionModal
        isOpen={isActionModalOpen}
        onClose={() => {
          setIsActionModalOpen(false);
          setSelectedStudent(null);
        }}
        student={isBatchMode ? null : selectedStudent}
        students={isBatchMode ? students.filter(s => selectedStudentIds.has(s.id)) : undefined}
        onUpdate={handleStudentUpdate}
        onChangePet={(student) => {
          setIsActionModalOpen(false);
          setSelectedStudent(null);
          setPetSelectionStudent(student);
          setIsPetModalOpen(true);
        }}
      />

      <PetSelectionModal
        isOpen={isPetModalOpen}
        onClose={() => setIsPetModalOpen(false)}
        studentName={petSelectionStudent?.name || ''}
        studentId={petSelectionStudent?.id || 0}
        unlockedPets={students.find(s => s.id === petSelectionStudent?.id)?.unlockedPets}
        onSelect={() => currentClass && fetchStudents(currentClass.id)}
      />

      <RenamePetModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        student={renameStudent}
        onSuccess={() => currentClass && fetchStudents(currentClass.id)}
      />

      <StoreModal
        isOpen={isStoreModalOpen}
        onClose={() => setIsStoreModalOpen(false)}
        onUpdate={() => currentClass && fetchStudents(currentClass.id)}
      />

      <GrowthRecordsModal
        isOpen={isGrowthRecordsModalOpen}
        onClose={() => setIsGrowthRecordsModalOpen(false)}
        classId={currentClass?.id || 0}
      />

      <ReportsModal
        isOpen={isReportsModalOpen}
        onClose={() => setIsReportsModalOpen(false)}
      />

      <ToolsModal
        isOpen={isToolsModalOpen}
        onClose={() => setIsToolsModalOpen(false)}
        onMinimizeTimer={() => setIsToolsModalOpen(false)}
        timerState={timerState}
        onTimerStateChange={setTimerState}
        students={students}
        onUpdate={(points, count) => {
          if (points > 0) playSound('pointsUp');
          else if (points < 0) playSound('pointsDown');

          if (currentClass) fetchStudents(currentClass.id);
          setFeedbackInfo({
            type: 'points',
            studentName: '',
            points,
            isBatch: true,
            count
          });
          setTimeout(() => setFeedbackInfo(null), 1500);
        }}
      />

      {/* Floating Mini Timer */}
      {isTimerMinimized && (
        <div
          className="fixed bottom-6 right-6 z-[90] flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-3 rounded-2xl shadow-lg cursor-pointer hover:scale-105 transition-transform animate-in slide-in-from-bottom-3 duration-300"
          onClick={() => setIsToolsModalOpen(true)}
        >
          <Timer className="w-5 h-5" />
          <span className="font-mono text-lg font-bold tabular-nums">
            {String(Math.floor(timerState.timeLeft / 60)).padStart(2, '0')}:{String(timerState.timeLeft % 60).padStart(2, '0')}
          </span>
          <span className="text-xs text-white/70">
            {timerState.timerMode === 'countdown' ? t('home.tools.timer.countdown', '倒计时') : t('home.tools.timer.stopwatch', '正计时')}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setTimerState(prev => ({ ...prev, isTimerRunning: false, timeLeft: 0, initialTime: 0 }));
            }}
            className="ml-1 p-1 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            title={t('home.tools.timer.stop', '停止')}
          >
            <Square className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Feedback Overlay */}
      {feedbackInfo && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 animate-in fade-in duration-200 pointer-events-none">
          <div className="relative bg-white/95 backdrop-blur-md rounded-[2rem] p-8 shadow-[0_0_50px_rgba(0,0,0,0.1)] text-center min-w-[240px] border border-white/50 animate-in zoom-in-90 slide-in-from-bottom-5 duration-300">
             <div className="text-6xl mb-4 animate-[bounce_1s_infinite]">
               {feedbackInfo.points > 0 ? '🎉' : '💸'}
             </div>
             <h3 className="text-2xl font-black text-gray-800 mb-2">
               {feedbackInfo.isBatch ? t('home.feedback.batchSuccess') : (feedbackInfo.points > 0 ? t('home.feedback.addSuccess') : t('home.feedback.deductSuccess'))}
             </h3>
             <div className={`text-5xl font-black mb-2 ${feedbackInfo.points > 0 ? 'text-green-500' : 'text-slate-500'}`}>
               {feedbackInfo.points > 0 ? '+' : ''}{feedbackInfo.points}
             </div>
             {feedbackInfo.studentName && (
               <p className="text-gray-500 font-bold text-lg">{feedbackInfo.studentName}</p>
             )}
             {feedbackInfo.isBatch && (
                <p className="text-gray-400 text-sm mt-1">{t('home.feedback.appliedTo', { count: feedbackInfo.count })}</p>
             )}
          </div>
        </div>
      )}

      {/* Level Up Overlay */}
      {levelUpInfo && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300" style={{zIndex: 999}}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          <div className="relative bg-gradient-to-br from-yellow-100 to-orange-100 rounded-[3rem] p-12 shadow-[0_0_100px_rgba(251,191,36,0.5)] text-center w-full max-w-md border-4 border-white/50 animate-in zoom-in-50 slide-in-from-bottom-10 duration-500 overflow-hidden">
            
            {/* Confetti & Stars Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" >
               <div className="absolute top-10 left-10 text-4xl animate-bounce delay-100">✨</div>
               <div className="absolute top-20 right-20 text-4xl animate-bounce delay-300">⭐</div>
               <div className="absolute bottom-20 left-20 text-4xl animate-bounce delay-500">🌟</div>
               <div className="absolute bottom-10 right-10 text-4xl animate-bounce delay-700">✨</div>
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle,rgba(255,255,255,0.8)_0%,rgba(255,255,255,0)_70%)] animate-pulse"></div>
            </div>

            <div className="relative z-10 flex flex-col items-center">
              <div className="text-8xl mb-6 animate-[bounce_1s_infinite]">🎉</div>
              
              <h2 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 mb-4 drop-shadow-sm">
                {levelUpInfo.newLevel === 10 ? t('home.levelUp.maxTitle') : t('home.levelUp.levelUpTitle')}
              </h2>
              
              <div className="bg-white/80 backdrop-blur-md px-8 py-4 rounded-3xl shadow-xl border border-white/50 transform rotate-[-2deg] hover:rotate-0 transition-transform duration-300 mb-8">
                {levelUpInfo.newLevel === 10 ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-4xl font-black text-orange-600">{t('home.levelUp.promote10')}</div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-400 px-4 py-1 text-base font-black text-white shadow">
                      {t('home.levelUp.maxAchieved')}
                    </div>
                  </div>
                ) : (
                  <span className="text-4xl font-black text-orange-600">
                    {t('home.levelUp.promoteN', { level: levelUpInfo.newLevel })}
                  </span>
                )}
              </div>

              {/* Evolution Animation */}
              {levelUpInfo.student.petType && (
                <div className="relative w-48 h-48 mb-6">
                  {/* Old Level Pet (Fading Out) */}
                  <div className="absolute inset-0 flex items-center justify-center animate-[fadeOut_1.2s_ease-in-out_forwards]">
                    <CosImage 
                      src={getPetImage(levelUpInfo.student.petType, levelUpInfo.oldLevel, petConfig)} 
                      alt="Old Pet" 
                      className="w-32 h-32 object-contain opacity-50 grayscale scale-90"
                    />
                  </div>
                  
                  {/* New Level Pet (Zooming In) */}
                  <div className="absolute inset-0 flex items-center justify-center z-10 animate-[zoomIn_2s_ease-out_1s_both]">
                    <div className="absolute inset-0 bg-yellow-400/30 blur-xl rounded-full animate-pulse"></div>
                    <CosImage 
                      src={getPetImage(levelUpInfo.student.petType, levelUpInfo.newLevel, petConfig)} 
                      alt="New Pet" 
                      className="w-40 h-40 object-contain drop-shadow-2xl dragon-animate"
                    />
                  </div>
                  
                  {/* Evolution Particles */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-full border-4 border-yellow-300 rounded-full animate-[ping_2s_ease-out_1s_infinite] opacity-50"></div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 text-orange-700/60 font-medium">
                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                {t('home.levelUp.footer', { name: levelUpInfo.student.name, status: levelUpInfo.newLevel === 10 ? t('home.levelUp.maxForm') : t('home.levelUp.stronger') })}
                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Prestige Overlay */}
      {prestigeInfo && fullLevelStudent && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
          <div 
            className="relative bg-white rounded-[3rem] p-1 shadow-[0_0_100px_rgba(251,191,36,0.6)] w-full max-w-sm animate-in zoom-in-50 slide-in-from-bottom-10 duration-500 overflow-hidden"
            style={{ 
              transform: typeof window !== 'undefined' ? (window.innerHeight < 650 ? 'scale(0.7)' : window.innerHeight < 800 ? 'scale(0.8)' : 'none') : 'none', 
              transformOrigin: 'center center' 
            }}
          >
            <div className="bg-gradient-to-b from-orange-50 to-white rounded-[2.8rem] p-8 text-center relative overflow-hidden h-full flex flex-col items-center">
              
              {/* Confetti */}
              <div className="absolute top-8 left-1/2 -translate-x-1/2 text-5xl animate-bounce z-10">🎉</div>
              
              {/* Stars */}
              <div className="absolute top-10 left-10 text-yellow-400 text-2xl animate-pulse">✨</div>
              <div className="absolute bottom-20 right-8 text-pink-400 text-xl animate-pulse delay-300">✨</div>
              <div className="absolute top-20 right-10 text-blue-400 text-lg animate-pulse delay-500">✨</div>

              <div className="mt-12 mb-2 flex items-center gap-2 justify-center">
                <span className="text-3xl font-black text-orange-500">🎉</span>
                <h2 className="text-3xl font-black text-orange-500">{t('home.prestige.title')}</h2>
                <span className="text-3xl font-black text-pink-400">✨</span>
              </div>
              
              <p className="text-indigo-900 font-bold mb-8">{t('home.prestige.congrats', { name: prestigeInfo.student.name })}</p>

              {/* Pet Circle */}
              <div className="relative w-48 h-48 mb-4">
                <div className="absolute inset-0 rounded-full border-4 border-orange-100 animate-[spin_10s_linear_infinite]"></div>
                <div className="absolute inset-2 rounded-full border-2 border-orange-200 animate-[ping_3s_infinite]"></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-b from-orange-50 to-white shadow-inner flex items-center justify-center overflow-hidden">
                  <CosImage 
                    src={getPetImage(prestigeInfo.student.petType || '', 10, petConfig)} 
                    alt="Pet" 
                    className="w-32 h-32 object-cover"
                  />
                </div>
              </div>

              <h3 className="text-2xl font-black text-gray-800 mb-2">{getPetName(prestigeInfo.student.petType || '')}</h3>
              <div className="flex items-center gap-2 text-gray-400 text-xs font-medium mb-8">
                <span>{t('home.prestige.petBadge')}</span>
              </div>

              {/* Badge Reward */}
              <div className="flex flex-col gap-2 mb-2 w-full">
                <div className="flex items-center gap-3 bg-orange-50 px-6 py-3 rounded-2xl">
                  <Award className="w-8 h-8 text-orange-500" />
                  <div className="text-left">
                    <div className="text-orange-500 font-bold">{t('home.prestige.badgeReward')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-yellow-50 px-6 py-3 rounded-2xl">
                  <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Coins className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  </div>
                  <div className="text-left">
                    <div className="text-yellow-600 font-bold">{t('home.prestige.coinsReward')}</div>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mb-6">{t('home.prestige.note')}</p>

              <div className="flex gap-3 w-full">
                <button 
                  onClick={() => {
                    setPrestigeInfo(null);
                    setFullLevelStudent(null);
                  }}
                  className="flex-1 py-3 bg-white border border-gray-200 text-gray-500 font-bold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  {t('home.prestige.later')}
                </button>
                <button 
                  onClick={handlePrestigeCollect}
                  disabled={isPrestigeCollecting}
                  className={`flex-1 py-3 bg-gradient-to-r from-orange-400 to-pink-500 text-white font-bold rounded-xl shadow-lg shadow-orange-200 transition-all flex items-center justify-center gap-2 ${isPrestigeCollecting ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-orange-300 transform hover:-translate-y-0.5'}`}
                >
                  <Award className="w-4 h-4" />
                  {t('home.prestige.collect')}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Sort Modal (Mobile) */}
      {isSortModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:hidden">
          <div 
            className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSortModalOpen(false)}
          ></div>
          <div className="relative bg-white rounded-[2rem] shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 text-gray-800">
                <ArrowUpDown className="w-6 h-6 text-pink-500" />
                <h2 className="text-xl font-bold">{t('home.sort.title')}</h2>
              </div>
              <button 
                onClick={() => setIsSortModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {[
                { value: 'score_desc', label: t('home.sort.score') },
                { value: 'name_asc', label: t('home.sort.name') }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSortMode(option.value as 'name_asc' | 'score_desc');
                    setIsSortModalOpen(false);
                  }}
                  className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${
                    sortMode === option.value 
                      ? 'bg-pink-50 text-pink-600 font-bold border-2 border-pink-200' 
                      : 'bg-gray-50 text-gray-600 font-medium border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <span>{option.label}</span>
                  {sortMode === option.value && <Check className="w-5 h-5" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
